/* import __COLOCATED_TEMPLATE__ from './sq-field-checkboxes.hbs'; */
/**
## Field Checkboxes

`SqFieldCheckboxesComponent` is a container for one or multiple checkboxes that renders an
[sq-option-checkbox](./sq-option-checkbox.html) for each option in the `options` array
passed to it. It can read the following keys:

```
{
  label: 'My Checkbox',
  labelSecondary: 'Some additional helpful text', //optional
  name: 'my-checkbox',
  value: 'checkbox-value',
  disabled: true/false, //default is false
  checked: true/false/'indeterminate', //default is false
};

```

```example
@title Basic Example

@controller-properties
{
  options: [
    {
      name: 'first',
      checked: false,
      label: 'First'
    },
    {
      name: 'second',
      checked: true,
      label: 'Second'
    },
    {
      name: 'third',
      checked: 'indeterminate',
      label: 'Third'
    },
    {
      name: 'fourth',
      checked: false,
      label: 'Fourth',
      disabled: true
    }
  ],
  actions: {
    checkboxesChanged(e) {
      $('.action-output').text('Checkbox clicked');
    }
  }
}

@hbs
{{sq-field-checkboxes options=options
                      onChange=(action "checkboxesChanged")}}
<p class="action-output"></p>
```

```example
@title Example with custom checkbox format
@description You can use the contextual components to yield out custom formatted
checkbox options. For contextual checkbox options, you can access the properties of
the individual option within the yielded `checkbox` by using `checkbox.props` in
your template

@controller-properties
{
  options: [
    {
      name: 'first',
      checked: false,
      label: 'First',
      someOtherPropertyName: 'This is the first one'
    },
    {
      name: 'second',
      checked: true,
      label: 'Second',
      someOtherPropertyName: 'This is the second one'
    },
    {
      name: 'third',
      checked: 'indeterminate',
      label: 'Third',
      someOtherPropertyName: 'This is the third one'
    }
  ]
}

@hbs
<h4>Basic Block</h4>
{{#sq-field-checkboxes options=options as |checkbox|}}
  {{#checkbox.option}}
    <strong>{{checkbox.props.someOtherPropertyName}}</strong>
  {{/checkbox.options}}
{{/sq-field-checkboxes}}

<h4>Contextual checkbox option components</h4>
{{#sq-field-checkboxes options=options as |checkbox|}}
  {{#checkbox.option as |opt|}}
    {{opt.label text=checkbox.props.label}}
    {{#opt.label-secondary}}
      <strong>{{checkbox.props.someOtherPropertyName}}</strong>
    {{/opt.label-secondary}}
  {{/checkbox.options}}
{{/sq-field-checkboxes}}
```


### Parameters
@param {String} name - identifier for this *set* of checkboxes
@param {Array} options - Ember array of objects to represent the checkboxes.
@param {Boolean} [disabled=false] - Set to `true` to disable the entire checkbox set.
@param {Action} onChange - Fired whenever there's any changed checkbox value in the set. (changedOptionCheckedState, mutatedOptionsArray, changedOptionIndex, changedOptionValue, changedOption) Parameters include he checked value of the changed option, the new options array, the index of the option that changed, the option's value, and the option itself.


Sass dependencies at:
`app/assets/stylesheets/bridgeport/components/form-elements/formfield/_formfield-checkbox.sass`
*/

import Component from '@ember/component';
import { set } from '@ember/object';
import FieldElementSupport from '../utils/field-element-support';
import FieldCheckboxFocusSupport from '../utils/field-checkbox-focus-support';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend(FieldElementSupport, FieldCheckboxFocusSupport, {
  tagName: 'fieldset',
  disabled: false,
  classNames: ['option-set', 'checkbox-set'],
  /* You may notice that the parameters passed into this function are in a
  different order from those that are sent out in the onChange action.
  This is because the first param `index`, is passed in through this component's
  template, while the following 2 come directly from the `sq-checkbox` component.
  We pass `checked` as the first param to the onChange action so that consuming
  components can use the (mut) template helper instead of needing to write a
  separate action to consume a second or third param */
  actions: {
    handleChange(index, checked, value) {
      set(this.get('options')[index], 'checked', checked);
      if (this.get('onChange')) {
        this.get('onChange')(checked, this.get('options'), index, value, this.get('options')[index]);
      }
    }
  }
});